import { Checkbox, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useRichTextEditorContext } from '@mantine/tiptap';
import { IconTrashX } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import Button from 'components/Button';

interface FormValues {
  unique: boolean;
}

/**
 * The device-loop configurator form.
 */
export default function DeviceLoopConfigurator({ onClose }: { onClose: () => void }) {
  const { editor } = useRichTextEditorContext();

  const initialValues = useMemo(() => {
    const attrs = editor.getAttributes('deviceLoop');
    const unique = attrs?.['data-unique'] ?? '';

    return {
      unique: unique === 'true' || unique === true,
    };
  }, []);

  const form = useForm<FormValues>({ initialValues });

  const submit = useCallback(
    ({ unique }: FormValues) => editor.chain().focus().setDeviceLoopUnique(unique).run(),
    [editor]
  );

  const submitAndClose = useCallback(
    (values: FormValues) => {
      submit(values);
      onClose();
    },
    [submit, onClose]
  );

  const removeDeviceLoop = useCallback(() => {
    editor.chain().focus().unsetDeviceLoop().run();
    onClose();
  }, [editor, onClose]);

  return (
    <form onSubmit={form.onSubmit(submitAndClose)}>
      <Stack spacing={16} pt={8}>
        <Checkbox
          size="sm"
          label={
            <Group spacing={4}>
              Vynechať duplicitné bloky{' '}
              <QuestionMarkTooltip
                withinPortal
                multiline
                width={320}
                label="Ak je pole zaškrtnuté, vo výslednom dokumente sa nebudú opakovať bloky, ktoré sú zhodné s niektorým z predchádzajúcich blokov."
              />
            </Group>
          }
          {...form.getInputProps('unique', { type: 'checkbox' })}
        />
        <Group pt={8} spacing={16} position="apart">
          <Button
            type="button"
            onClick={removeDeviceLoop}
            variant="danger-secondary"
            leftIcon={<IconTrashX size={16} />}
          >
            Odstrániť opakovač
          </Button>
          <Group spacing={16}>
            <Button type="button" onClick={onClose} variant="subtle-gray">
              Zrušiť
            </Button>
            <Button type="submit" variant="primary">
              Nastaviť
            </Button>
          </Group>
        </Group>
      </Stack>
    </form>
  );
}
